import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Spinner } from '@/components/ui/spinner';
import { AlertCircle, Check, CheckCircle2, X, FileImage } from 'lucide-react';
import { teamApi, AltReviewImage, AltReviewPage, AltReviewHistoryItem, AltReviewHistoryPage } from '@/api/teamApi';
import { API_BASE_URL as BASE } from '@/api/apiConfig';

export default function TeamAltReviewPage() {
  const { myTeams } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  // myTeams는 AuthContext에서 비동기로 채워지므로(마운트 시점엔 보통 빈 배열),
  // 로드가 끝난 뒤에도 소속 팀을 자동 선택할 수 있도록 별도로 동기화한다.
  useEffect(() => {
    if (selectedTeamId === null && myTeams.length > 0) {
      setSelectedTeamId(myTeams[0].teamId);
    }
  }, [myTeams, selectedTeamId]);

  const myRole = myTeams.find(t => t.teamId === selectedTeamId)?.myRole ?? null;
  const isOwner = myRole === 'OWNER';

  // teamId를 함께 저장해, 팀 전환 시 "직전 팀에 대해 확인된 값"이 새 팀에
  // 잘못 적용되는 것을 렌더링 단계에서 원천적으로 막는다(파생값이 selectedTeamId와
  // 다른 teamId를 가리키면 무조건 null=미확인으로 취급).
  const [workflowStatus, setWorkflowStatus] = useState<{ teamId: number; enabled: boolean } | null>(null);
  const workflowEnabled = workflowStatus && workflowStatus.teamId === selectedTeamId
    ? workflowStatus.enabled
    : null;

  useEffect(() => {
    if (!selectedTeamId) return;
    let cancelled = false;
    teamApi.getTeam(selectedTeamId)
      .then(res => { if (!cancelled) setWorkflowStatus({ teamId: selectedTeamId, enabled: !!res.data.approvalWorkflowEnabled }); })
      .catch(() => { if (!cancelled) setWorkflowStatus({ teamId: selectedTeamId, enabled: false }); });
    return () => { cancelled = true; };
  }, [selectedTeamId]);

  const [tab, setTab] = useState<'pending' | 'history'>('pending');

  const [data, setData] = useState<AltReviewPage | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [historyData, setHistoryData] = useState<AltReviewHistoryPage | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async (teamId: number, pageNum: number, owner: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = owner
        ? await teamApi.getPendingAltReviews(teamId, pageNum, 20)
        : await teamApi.getMyPendingAltReviews(teamId, pageNum, 20);
      setData(res.data);
      setPage(pageNum);
    } catch (err: any) {
      setError(err.response?.data?.message || '검수대기 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (teamId: number, pageNum: number, owner: boolean) => {
    setHistoryLoading(true);
    setError(null);
    try {
      const res = owner
        ? await teamApi.getAltReviewHistory(teamId, pageNum, 20)
        : await teamApi.getMyAltReviewHistory(teamId, pageNum, 20);
      setHistoryData(res.data);
      setHistoryPage(pageNum);
    } catch (err: any) {
      setError(err.response?.data?.message || '처리 완료 이력 조회에 실패했습니다.');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    // 팀/역할을 바꾸면 이전 팀(또는 이전 관점)의 검수대기/처리 완료 목록이
    // 남아있지 않도록 비운다. 검수대기 목록은 워크플로우가 켜진 팀에서만
    // 조회 가능(백엔드 IllegalStateException)하므로 여기서 초기화만 하고,
    // 실제 조회는 workflowEnabled가 확정된 뒤 아래 effect에서 수행한다.
    setData(null);
    setHistoryData(null);
    setTab('pending');
  }, [selectedTeamId, myRole]);

  useEffect(() => {
    if (!selectedTeamId || !myRole) return;
    if (workflowEnabled === true) {
      fetchData(selectedTeamId, 0, isOwner);
    }
    // workflowEnabled === false: 검수대기 목록은 조회하지 않고 안내만 표시.
    // workflowEnabled === null: 아직 확인 중 - 확인되면 이 effect가 다시 실행된다.
  }, [selectedTeamId, myRole, isOwner, workflowEnabled, fetchData]);

  useEffect(() => {
    // 처리 완료(감사) 이력은 워크플로우가 꺼져 있어도 항상 조회 가능하다 -
    // 팀장이 검수를 끝내고 워크플로우를 끄더라도 방금 처리한 내역은 계속
    // 확인할 수 있어야 하기 때문(AltReviewService.getReviewedHistory 참고).
    if (selectedTeamId && myRole && tab === 'history' && !historyData) {
      fetchHistory(selectedTeamId, 0, isOwner);
    }
  }, [selectedTeamId, myRole, isOwner, tab, historyData, fetchHistory]);

  const handleAction = async (imageId: number, action: 'approve' | 'reject') => {
    if (!selectedTeamId) return;
    if (action === 'reject') {
      const note = window.prompt('거부 사유(선택)를 입력해 주세요.');
      if (note === null) return; // 취소 시 아무 것도 하지 않음
      setActingId(imageId);
      try {
        await teamApi.rejectAltReview(selectedTeamId, imageId, note || undefined);
        showToast('거부 처리되었습니다. "처리 완료" 탭에서 확인할 수 있어요.');
        await fetchData(selectedTeamId, page, isOwner);
        setHistoryData(null); // 다음에 처리 완료 탭을 열면 최신 목록을 다시 불러오도록
      } catch (err: any) {
        setError(err.response?.data?.message || '처리 중 오류가 발생했습니다.');
      } finally {
        setActingId(null);
      }
      return;
    }
    setActingId(imageId);
    try {
      await teamApi.approveAltReview(selectedTeamId, imageId);
      showToast('승인 처리되었습니다. "처리 완료" 탭에서 확인할 수 있어요.');
      await fetchData(selectedTeamId, page, isOwner);
      setHistoryData(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '처리 중 오류가 발생했습니다.');
    } finally {
      setActingId(null);
    }
  };

  const getImageUrl = (src?: string | null) => {
    if (!src) return '';
    if (src.startsWith('http')) return src;
    return `${BASE}${src.startsWith('/') ? '' : '/'}${src}`;
  };

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const headerDescription = isOwner
    ? '팀원이 생성한 ALT 텍스트를 검수하고 승인/거부합니다 (감사/추적용 - 승인 전에도 ALT는 그대로 사용됩니다)'
    : '내가 생성한 ALT 텍스트의 검수 현황을 확인합니다 (읽기 전용 - 승인/거부는 팀장만 할 수 있습니다)';

  return (
    <>
      <PageTitle title="ALT 승인 검수" />
      <PageContainer maxWidth="full">
        <PageHeader
          title={menuName || 'ALT 승인 검수'}
          description={headerDescription}
          icon={menuIcon}
        />

        {toast && (
          <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
            <Alert className="bg-green-50 border-green-500">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{toast}</AlertDescription>
            </Alert>
          </div>
        )}

        {myTeams.length === 0 ? (
          <Alert>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              소속된 팀이 없습니다. ALT 승인 검수는 팀 소속 사용자만 이용할 수 있습니다.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {myTeams.length > 1 && (
              <Card>
                <CardContent className="pt-6">
                  <Select
                    value={selectedTeamId ? String(selectedTeamId) : undefined}
                    onValueChange={(v) => setSelectedTeamId(Number(v))}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="팀 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {myTeams.map(t => (
                        <SelectItem key={t.teamId} value={String(t.teamId)}>{t.teamName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Card>
              <CardContent className="pt-6">
                <Tabs value={tab} onValueChange={(v) => setTab(v as 'pending' | 'history')}>
                  <TabsList className="grid grid-cols-2 w-64 mb-4">
                    <TabsTrigger value="pending">검수대기</TabsTrigger>
                    <TabsTrigger value="history">처리 완료</TabsTrigger>
                  </TabsList>

                  <TabsContent value="pending">
                    {workflowEnabled === false ? (
                      <Alert variant="destructive">
                        <AlertCircle className="w-4 h-4" />
                        <AlertDescription>
                          {isOwner
                            ? '이 팀은 "ALT 승인 워크플로우 사용"이 꺼져 있어 검수대기 목록을 볼 수 없습니다. 팀 관리 페이지에서 워크플로우를 켜면 이용할 수 있습니다.'
                            : '이 팀은 "ALT 승인 워크플로우 사용"이 꺼져 있어 검수대기 현황을 볼 수 없습니다. 필요하다면 팀장에게 문의해 주세요.'}
                        </AlertDescription>
                      </Alert>
                    ) : loading || workflowEnabled === null ? (
                      <div className="flex justify-center py-10">
                        <Spinner size="md" />
                      </div>
                    ) : !data || data.content.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <FileImage className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>검수대기 중인 ALT 텍스트가 없습니다.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-500">검수대기 {data.totalElements.toLocaleString()}건</p>
                        {data.content.map((item: AltReviewImage) => (
                          <Card key={item.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex flex-col md:flex-row gap-4">
                                <img
                                  src={getImageUrl(item.displayImageUrl || item.imageSrc)}
                                  alt=""
                                  className="w-full md:w-32 h-32 object-cover rounded-lg bg-gray-100"
                                  onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                />
                                <div className="flex-1 space-y-2 min-w-0">
                                  <div className="flex gap-2 flex-wrap items-center">
                                    <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs">⏳ 검수대기</Badge>
                                    {item.altGenerationType && (
                                      <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">{item.altGenerationType}</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 truncate">
                                    <span className="font-semibold">출처:</span> {item.url}
                                  </p>
                                  <p className="text-sm text-gray-800 p-2 rounded bg-gray-50 break-words">
                                    {item.generatedAlt || '(ALT 없음)'}
                                  </p>
                                  {isOwner && (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => handleAction(item.id, 'approve')}
                                        disabled={actingId === item.id}
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        {actingId === item.id ? <Spinner size="xs" className="mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                                        승인
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleAction(item.id, 'reject')}
                                        disabled={actingId === item.id}
                                        className="text-red-600 border-red-300 hover:bg-red-50"
                                      >
                                        <X className="w-4 h-4 mr-1" />
                                        거부
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        {(data.totalPages || 1) > 1 && (
                          <div className="flex justify-center items-center gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => selectedTeamId && fetchData(selectedTeamId, page - 1, isOwner)}
                              disabled={page === 0 || loading}
                            >
                              이전
                            </Button>
                            <span className="text-sm text-gray-600">{page + 1} / {data.totalPages}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => selectedTeamId && fetchData(selectedTeamId, page + 1, isOwner)}
                              disabled={page >= (data.totalPages || 1) - 1 || loading}
                            >
                              다음
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="history">
                    {historyLoading ? (
                      <div className="flex justify-center py-10">
                        <Spinner size="md" />
                      </div>
                    ) : !historyData || historyData.content.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <FileImage className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>아직 승인/거부 처리한 내역이 없습니다.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-500">처리 완료 {historyData.totalElements.toLocaleString()}건</p>
                        {historyData.content.map((item: AltReviewHistoryItem) => (
                          <Card key={item.imageId} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex flex-col md:flex-row gap-4">
                                <img
                                  src={getImageUrl(item.displayImageUrl || item.imageSrc)}
                                  alt=""
                                  className="w-full md:w-32 h-32 object-cover rounded-lg bg-gray-100"
                                  onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                />
                                <div className="flex-1 space-y-2 min-w-0">
                                  <div className="flex gap-2 flex-wrap items-center">
                                    {item.status === 'APPROVED' ? (
                                      <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">✅ 승인됨</Badge>
                                    ) : (
                                      <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs">❌ 거부됨</Badge>
                                    )}
                                    <span className="text-xs text-gray-400">{formatDateTime(item.reviewedAt)} 처리</span>
                                  </div>
                                  <p className="text-sm text-gray-600 truncate">
                                    <span className="font-semibold">출처:</span> {item.url}
                                  </p>
                                  <p className="text-sm text-gray-800 p-2 rounded bg-gray-50 break-words">
                                    {item.generatedAlt || '(ALT 없음)'}
                                  </p>
                                  {item.note && (
                                    <p className="text-xs text-gray-500 p-2 rounded bg-yellow-50 border border-yellow-100">
                                      사유: {item.note}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        {(historyData.totalPages || 1) > 1 && (
                          <div className="flex justify-center items-center gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => selectedTeamId && fetchHistory(selectedTeamId, historyPage - 1, isOwner)}
                              disabled={historyPage === 0 || historyLoading}
                            >
                              이전
                            </Button>
                            <span className="text-sm text-gray-600">{historyPage + 1} / {historyData.totalPages}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => selectedTeamId && fetchHistory(selectedTeamId, historyPage + 1, isOwner)}
                              disabled={historyPage >= (historyData.totalPages || 1) - 1 || historyLoading}
                            >
                              다음
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </>
        )}
      </PageContainer>
    </>
  );
}
